// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Button, Col, Form, Input, Row, Spin, Tabs} from "antd";
import {
  ArrowLeftOutlined
  // LockOutlined,
  // UserOutlined
} from "@ant-design/icons";
// import * as UserWebauthnBackend from "../backend/UserWebauthnBackend";
import OrganizationSelect from "../common/select/OrganizationSelect";
// import * as Conf from "../Conf";
import * as AuthBackend from "./AuthBackend";
import * as OrganizationBackend from "../backend/OrganizationBackend";
import * as ApplicationBackend from "../backend/ApplicationBackend";
import * as Provider from "./Provider";
// import * as ProviderButton from "./ProviderButton";
import * as Util from "./Util";
import * as Setting from "../Setting";
import {authConfig} from "../auth/Auth";

import {AgreementModal} from "../common/modal/AgreementModal";
import SelfLoginButton from "./SelfLoginButton";
import i18next from "i18next";
import CustomGithubCorner from "../common/CustomGithubCorner";
import {SendCodeInput} from "../common/SendCodeInput";
import {CountryCodeSelect} from "../common/select/CountryCodeSelect";

// import LanguageSelect from "../common/select/LanguageSelect";
import {CaptchaModal} from "../common/modal/CaptchaModal";
import {CaptchaRule} from "../common/modal/CaptchaModal";
import RedirectForm from "../common/RedirectForm";
import {MfaAuthVerifyForm, NextMfa, RequiredMfa} from "./MfaAuthVerifyForm";
import * as UserBackend from "../backend/UserBackend";

class LoginPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      classes: props,
      type: props.type,
      applicationName: props.applicationName ?? (props.match?.params?.applicationName ?? null),
      owner: props.owner ?? (props.match?.params?.owner ?? null),
      mode: props.mode ?? (props.match?.params?.mode ?? null), // "signup" or "signin"
      msg: null,
      username: null,
      validEmailOrPhone: false,
      validEmail: false,
      loginMethod: Setting.getLanguage() === "zh" ? "phone" : "password",
      enableCaptchaModal: CaptchaRule.Never,
      openCaptchaModal: false,
      verifyCaptcha: undefined,
      samlResponse: "",
      relayState: "",
      redirectUrl: "",
      isTermsOfUseVisible: false,
      termsOfUseContent: "",
      orgChoiceMode: new URLSearchParams(props.location?.search).get("orgChoiceMode") ?? null,
      countryCode: "CN",
      // noCaptchModal: false,
    };

    if (this.state.type === "cas" && props.match?.params.casApplicationName !== undefined) {
      this.state.owner = props.match?.params?.owner;
      this.state.applicationName = props.match?.params?.casApplicationName;
    }

    this.form = React.createRef();
    this.captcaModalRef = React.createRef();
  }

  componentDidMount() {
    if (this.getApplicationObj() === undefined) {
      if (this.state.type === "login" || this.state.type === "cas" || this.state.type === "saml") {
        this.getApplication();
      } else if (this.state.type === "code") {
        this.getApplicationLogin();
      } else {
        Setting.showMessage("error", `Unknown authentication type: ${this.state.type}`);
      }
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.application !== this.props.application) {
      const captchaProviderItems = this.getCaptchaProviderItems(this.props.application);
      if (captchaProviderItems) {
        this.setState({enableCaptchaModal: CaptchaRule.Dynamic});

        // if (captchaProviderItems.some(providerItem => providerItem.rule === "Always")) {
        //   this.setState({enableCaptchaModal: CaptchaRule.Always});
        // } else if (captchaProviderItems.some(providerItem => providerItem.rule === "Dynamic")) {
        //   this.setState({enableCaptchaModal: CaptchaRule.Dynamic});
        // } else {
        //   this.setState({enableCaptchaModal: CaptchaRule.Never});
        // }
      }

      if (this.props.account && this.props.account.owner === this.props.application?.organization) {
        const params = new URLSearchParams(this.props.location.search);
        const silentSignin = params.get("silentSignin");
        if (silentSignin !== null) {
          this.sendSilentSigninData("signing-in");

          const values = {};
          values["application"] = this.props.application.name;
          this.login(values);
        }

        if (params.get("popup") === "1") {
          window.addEventListener("beforeunload", () => {
            this.sendPopupData({type: "windowClosed"}, params.get("redirect_uri"));
          });
        }

        if (this.props.application.enableAutoSignin) {
          const values = {};
          values["application"] = this.props.application.name;
          this.login(values);
        }
      }
    }
  }

  checkCaptchaStatus(values) {
    AuthBackend.getCaptchaStatus(values)
      .then((res) => {
        if (res.status === "ok") {
          if (res.data) {
            this.setState({
              openCaptchaModal: true,
              values: values,
            });
            return null;
          }
        }
        this.login(values);
      });
  }

  getApplicationLogin() {
    const oAuthParams = Util.getOAuthGetParameters();
    AuthBackend.getApplicationLogin(oAuthParams)
      .then((res) => {
        if (res.status === "ok") {
          const application = res.data;
          this.onUpdateApplication(application);
        } else {
          this.onUpdateApplication(null);
          this.setState({
            msg: res.msg,
          });
        }
      });
  }

  getApplication() {
    if (this.state.applicationName === null) {
      return null;
    }

    if (this.state.owner === null || this.state.type === "saml") {
      ApplicationBackend.getApplication("admin", this.state.applicationName)
        .then((application) => {
          this.onUpdateApplication(application);
        });
    } else {
      OrganizationBackend.getDefaultApplication("admin", this.state.owner)
        .then((res) => {
          if (res.status === "ok") {
            const application = res.data;
            this.onUpdateApplication(application);
            this.setState({
              applicationName: res.data.name,
            });
          } else {
            this.onUpdateApplication(null);
            Setting.showMessage("error", res.msg);
          }
        });
    }
  }

  getApplicationObj() {
    return this.props.application;
  }

  onUpdateAccount(account) {
    this.props.onUpdateAccount(account);
  }

  onUpdateApplication(application) {
    this.props.onUpdateApplication(application);
  }

  // parseOffset(offset) {
  //   if (offset === 2 || offset === 4 || Setting.inIframe() || Setting.isMobile()) {
  //     return "0 auto";
  //   }
  //   if (offset === 1) {
  //     return "0 10%";
  //   }
  //   if (offset === 3) {
  //     return "0 60%";
  //   }
  // }

  populateOauthValues(values) {
    if (this.getApplicationObj()?.organization) {
      values["organization"] = this.getApplicationObj().organization;
    }

    const oAuthParams = Util.getOAuthGetParameters();

    values["type"] = oAuthParams?.responseType ?? this.state.type;

    if (oAuthParams?.samlRequest) {
      values["samlRequest"] = oAuthParams.samlRequest;
      values["type"] = "saml";
      values["relayState"] = oAuthParams.relayState;
    }
  }

  sendPopupData(message, redirectUri) {
    const params = new URLSearchParams(this.props.location.search);
    if (params.get("popup") === "1") {
      window.opener.postMessage(message, redirectUri);
    }
  }

  postCodeLoginAction(resp) {
    const application = this.getApplicationObj();
    const ths = this;
    const oAuthParams = Util.getOAuthGetParameters();
    const code = resp.data;
    const concatChar = oAuthParams?.redirectUri?.includes("?") ? "&" : "?";
    const noRedirect = oAuthParams.noRedirect;

    if (Setting.hasPromptPage(application) || resp.msg === RequiredMfa) {
      AuthBackend.getAccount()
        .then((res) => {
          if (res.status === "ok") {
            const account = res.data;
            account.organization = res.data2;
            this.onUpdateAccount(account);

            if (resp.msg === RequiredMfa) {
              Setting.goToLink(`/prompt/${application.name}?redirectUri=${oAuthParams.redirectUri}&code=${code}&state=${oAuthParams.state}&promptType=mfa`);
              return;
            }

            if (Setting.isPromptAnswered(account, application)) {
              Setting.goToLink(`${oAuthParams.redirectUri}${concatChar}code=${code}&state=${oAuthParams.state}`);
            } else {
              Setting.goToLinkSoft(ths, `/prompt/${application.name}?redirectUri=${oAuthParams.redirectUri}&code=${code}&state=${oAuthParams.state}`);
            }
          } else {
            Setting.showMessage("error", `${i18next.t("application:Failed to sign in")}: ${res.msg}`);
          }
        });
    } else {
      if (noRedirect === "true") {
        window.close();
        const newWindow = window.open(`${oAuthParams.redirectUri}${concatChar}code=${code}&state=${oAuthParams.state}`);
        if (newWindow) {
          setInterval(() => {
            if (!newWindow.closed) {
              newWindow.close();
            }
          }, 1000);
        }
      } else {
        Setting.goToLink(`${oAuthParams.redirectUri}${concatChar}code=${code}&state=${oAuthParams.state}&language=${Setting.getLanguage()}`);
        this.sendPopupData({type: "loginSuccess", data: {code: code, state: oAuthParams.state}}, oAuthParams.redirectUri);
      }
    }
  }

  onFinish(values) {
    const application = this.getApplicationObj();
    values["application"] = application.name;
    values["organization"] = application.organization;
    if (values.password) {
      this.setState({
        openCaptchaModal: true,
        values: values,
      });
    } else {
      this.login(values);
    }

    // this.setState({
    //   openCaptchaModal: true,
    //   values: values,
    // });
    // this.checkCaptchaStatus(values);
    // 
  }

  login(values) {
    // here we are supposed to determine whether Casdoor is working as an OAuth server or CAS server
    if (this.state.type === "cas") {
      // CAS
      const casParams = Util.getCasParameters();
      values["type"] = this.state.type;
      AuthBackend.loginCas(values, casParams).then((res) => {
        if (res.status === "ok") {
          let msg = "Logged in successfully. ";
          if (casParams.service === "") {
            // If service was not specified, Casdoor must display a message notifying the client that it has successfully initiated a single sign-on session.
            msg += "Now you can visit apps protected by Casdoor.";
          }
          Setting.showMessage("success", msg);

          if (casParams.service !== "") {
            const st = res.data;
            const newUrl = new URL(casParams.service);
            newUrl.searchParams.append("ticket", st);
            window.location.href = newUrl.toString();
          }
        } else {
          Setting.showMessage("error", `${i18next.t("application:Failed to sign in")}: ${res.msg}`);
        }
      });
    } else {
      // OAuth
      const oAuthParams = Util.getOAuthGetParameters();
      this.populateOauthValues(values);
      AuthBackend.login(values, oAuthParams)
        .then((res) => {
          const callback = (res) => {
            const responseType = values["type"];
            if (responseType === "login") {
              if (res.msg === RequiredMfa) {
                AuthBackend.getAccount().then((res) => {
                  if (res.status === "ok") {
                    const account = res.data;
                    account.organization = res.data2;
                    this.onUpdateAccount(account);
                  }
                });
                Setting.goToLink(`/prompt/${this.getApplicationObj().name}?promptType=mfa`);
              } else {
                Setting.showMessage("success", i18next.t("application:Logged in successfully"));
                const link = Setting.getFromLink();
                Setting.goToLink(link);
              }
            } else if (responseType === "code") {
              this.postCodeLoginAction(res);
            } else if (responseType === "token" || responseType === "id_token") {
              const amendatoryResponseType = responseType === "token" ? "access_token" : responseType;
              const accessToken = res.data;
              Setting.goToLink(`${oAuthParams.redirectUri}#${amendatoryResponseType}=${accessToken}&state=${oAuthParams.state}&token_type=bearer`);
            } else if (responseType === "saml") {
              if (res.data2.method === "POST") {
                this.setState({
                  samlResponse: res.data,
                  redirectUrl: res.data2.redirectUrl,
                  relayState: oAuthParams.relayState,
                });
              } else {
                const SAMLResponse = res.data;
                const redirectUri = res.data2.redirectUrl;
                Setting.goToLink(`${redirectUri}?SAMLResponse=${encodeURIComponent(SAMLResponse)}&RelayState=${oAuthParams.relayState}`);
              }
            }
          };

          if (res.status === "ok") {
            callback(res);
          } else if (res.status === NextMfa) {
            this.setState({
              getVerifyTotp: () => {
                return (
                  <MfaAuthVerifyForm
                    mfaProps={res.data}
                    formValues={values}
                    oAuthParams={oAuthParams}
                    application={this.getApplicationObj()}
                    onFail={() => {
                      Setting.showMessage("error", i18next.t("mfa:Verification failed"));
                    }}
                    onSuccess={(res) => callback(res)}
                  />);
              },
            });
          } else {
            Setting.showMessage("error", `${i18next.t("application:Failed to sign in")}: ${res.msg}`);
            // if (res.flag === "Account password error") {
            //   this.toSignupLink();
            // }
          }
        });
    }
  }

  isProviderVisible(providerItem) {
    if (this.state.mode === "signup") {
      return Setting.isProviderVisibleForSignUp(providerItem);
    } else {
      return Setting.isProviderVisibleForSignIn(providerItem);
    }
  }

  renderForm(method) {
    const application = this.getApplicationObj();
    // if (this.state.msg !== null) {
    //   return Util.renderMessage(this.state.msg);
    // }

    // if (this.state.mode === "signup" && !application.enableSignUp) {
    //   return (
    //     <Result
    //       status="error"
    //       title={i18next.t("application:Sign Up Error")}
    //       subTitle={i18next.t("application:The application does not allow to sign up new account")}
    //       extra={[
    //         <Button type="primary" key="signin"
    //           onClick={() => Setting.redirectToLoginPage(application, this.props.history)}>
    //           {
    //             i18next.t("login:Sign In")
    //           }
    //         </Button>,
    //       ]}
    //     >
    //     </Result>
    //   );
    // }

    // if (application.enablePassword) {
    // let loginWidth = 320;
    // if (Setting.getLanguage() === "fr") {
    //   loginWidth += 20;
    // } else if (Setting.getLanguage() === "es") {
    //   loginWidth += 40;
    // } else if (Setting.getLanguage() === "ru") {
    //   loginWidth += 10;
    // }

    return (
      <Form
        name="normal_login"
        initialValues={{
          organization: application.organization,
          application: application.name,
          countryCode: "CN",
        }}
        onFinish={(values) => {
          this.onFinish(values);
        }}
        onFinishFailed={(errorInfo) => console.log(errorInfo)}
        // style={{width: `${loginWidth}px`}}
        style={Setting.isMobile() ? {width: "300px", position: "relative", height: "calc(100vh - 150px)"} : {width: "360px"}}
        size="large"
        ref={this.form}
      >
        {method === "phone" ? <div style={{width: "100%", display: "flex", justifyContent: "flex-start", marginBottom: "10px"}}>
          <Form.Item
            name="countryCode"
            noStyle
            rules={[
              {
                required: true,
                message: i18next.t("signup:Please select your country code!"),
              },
            ]}
          >
            <CountryCodeSelect
              style={{width: "35%"}}
              initValue="CN"
              countryCodes={this.getApplicationObj().organizationObj.countryCodes}
              onChange={(e) => {
                this.setState({countryCode: e});
              }}
            />
          </Form.Item>
        </div> : null}
        <Row style={{minHeight: 130, alignItems: "center"}}>
          <Col span={24}>
            <Form.Item
              name="username"
              rules={[
                {
                  required: true,
                  message: (method === "phone") ? i18next.t("login:Please input your Phone!") : i18next.t("login:Please input your Email or username!"),
                },
                {
                  validator: (_, value) => {
                    // 手机验证码
                    if (method === "phone") {
                      if (!Setting.isValidPhone(value, this.state.countryCode)) {
                        this.setState({validEmailOrPhone: false});
                        return Promise.reject(i18next.t("login:The input is not valid phone number!"));
                      }
                      // if (Setting.isValidEmail(value)) {
                      //   this.setState({validEmail: true});
                      // } else {
                      //   this.setState({validEmail: false});
                      // }
                    }
                    // else {
                    //   // 邮箱
                    //   if (!Setting.isValidEmail(value)) {
                    //     // this.setState({validEmailOrPhone: false});
                    //     return Promise.reject(i18next.t("signup:The input is not valid Email!"));
                    //   }
                    // }
                    this.setState({validEmailOrPhone: true});
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                id="input"
                bordered={false}
                placeholder={(method === "phone") ? i18next.t("login:Enter your registered phone number") : i18next.t("login:Enter your email or username")}
                disabled={!application.enablePassword}
                onChange={e => {
                  this.setState({
                    username: e.target.value,
                  });
                }}
              />
            </Form.Item>
          </Col>
          {
            this.renderPasswordOrCodeInput(method)
          }
        </Row>
        <Form.Item>
          <Button
            htmlType="submit"
            style={{
              width: "100%",
              marginBottom: "5px",
              background: "linear-gradient(to right, #71B9A4, #318545)",
              color: "white",
              border: "none",
            }}
            disabled={!application.enablePassword}
          >
            {i18next.t("login:Sign In")}
          </Button>
          {this.renderCaptchaModal(application)}
          <div style={{marginTop: "18px"}}>
            {this.renderFooter(application)}
          </div>
          {method === "password" && <div style={{margin: "8px 0", textAlign: "left"}}>
            <span>{i18next.t("login:Forgot password")} &nbsp;{i18next.t("login:Go")}&nbsp;
              {
                Setting.renderForgetLink(application, i18next.t("forget:Change Password") + ">")
              }
            </span>
          </div>}
        </Form.Item>
        <a
          style={Setting.isMobile() ? {
            position: "absolute",
            bottom: "0px",
            left: "50%",
            translate: "-50%",
          } : {}}
          onClick={() => {
            this.setState({
              isTermsOfUseVisible: true,
            });
          }}
        >
          {method === "phone" ? i18next.t("login:《Privacy Policy》") : `${i18next.t("signup:Announcement")}`}
        </a>
        <AgreementModal
          layout={{}}
          closable
          open={this.state.isTermsOfUseVisible}
          cancelButtonProps={{style: {display: "none"}}}
          okText={i18next.t("login:Confirm")}
          loginMethod={method}
          onOk={() => {
            this.setState({
              isTermsOfUseVisible: false,
            });
          }}
          onCancel={() => {
            this.setState({
              isTermsOfUseVisible: false,
            });
          }}
        />
        {/* {renderAgreementFormItem(application, true, {}, this)} */}
      </Form>
    );
  }

  getCaptchaProviderItems(application) {
    const providers = application?.providers;

    if (providers === undefined || providers === null) {
      return null;
    }

    return providers.filter(providerItem => {
      if (providerItem.provider === undefined || providerItem.provider === null) {
        return false;
      }

      return providerItem.provider.category === "Captcha";
    });
  }

  handleOnOk(captchaType, captchaToken, clientSecret, ths) {
    const {values} = ths.state;
    // values["captchaType"] = captchaType;
    // values["captchaToken"] = captchaToken;
    // values["clientSecret"] = clientSecret;
    UserBackend.verifyCaptcha(captchaType, captchaToken, clientSecret).then((res) => {
      if (res) {
        ths.setState({
          openCaptchaModal: false,
        });
        ths.login(values);
      } else {
        ths.captcaModalRef?.loadCaptcha();
        ths.captcaModalRef?.setCaptchaToken("");
      }
    });
  }

  renderCaptchaModal(application) {
    const captchaProviderItems = this.getCaptchaProviderItems(application);
    const alwaysProviderItems = captchaProviderItems.filter(providerItem => providerItem.rule === "Always");
    const dynamicProviderItems = captchaProviderItems.filter(providerItem => providerItem.rule === "Dynamic");
    const provider = (alwaysProviderItems.length > 0
      ? alwaysProviderItems[0]?.provider
      : dynamicProviderItems[0]?.provider) || captchaProviderItems[0]?.provider;
    if (!provider) {
      // this.setState({
      //   noCaptchModal: true,
      // });
      return null;
    }

    return <CaptchaModal
      onRef={(ref) => {this.captcaModalRef = ref;}}
      owner={provider.owner}
      name={provider.name}
      visible={this.state.openCaptchaModal}
      onOk={(arg1, arg2, arg3) => this.handleOnOk(arg1, arg2, arg3, this)}
      onCancel={() => this.setState({openCaptchaModal: false})}
      isCurrentProvider={true}
    />;
  }

  // 底部-立即注册
  renderFooter(application) {
    return (
      <div style={{textAlign: "left"}}>
        {i18next.t("login:No account")}&nbsp;{i18next.t("login:Go")}&nbsp;
        {
          Setting.renderSignupLink(application, i18next.t("account:Sign Up") + ">")
        }
      </div>
    );
  }

  toSignupLink() {
    const application = this.getApplicationObj();
    let url;
    if (application === null) {
      url = null;
    } else if (!application.enablePassword && window.location.pathname.includes("/login/oauth/authorize")) {
      url = window.location.href.replace("/login/oauth/authorize", "/auto-signup/oauth/authorize");
    } else if (authConfig.appName === application.name) {
      url = "/signup";
    } else {
      if (application.signupUrl === "") {
        url = `/signup/${application.name}`;
      } else {
        url = application.signupUrl;
      }
    }
    sessionStorage.setItem("signinUrl", window.location.href);
    this.props.history.push(url);
  }

  sendSilentSigninData(data) {
    if (Setting.inIframe()) {
      const message = {tag: "Casdoor", type: "SilentSignin", data: data};
      window.parent.postMessage(message, "*");
    }
  }

  renderSignedInBox() {
    if (this.props.account === undefined || this.props.account === null) {
      this.sendSilentSigninData("user-not-logged-in");
      return null;
    }

    const application = this.getApplicationObj();
    if (this.props.account.owner !== application?.organization) {
      return null;
    }

    return (
      <div>
        <div style={{fontSize: 16, textAlign: "left"}}>
          {i18next.t("login:Continue with")}&nbsp;:
        </div>
        <br />
        <SelfLoginButton account={this.props.account} onClick={() => {
          const values = {};
          values["application"] = application.name;
          this.login(values);
        }} />
        <br />
        <br />
        <div style={{fontSize: 16, textAlign: "left"}}>
          {i18next.t("login:Or sign in with another account")}&nbsp;:
        </div>
      </div>
    );
  }

  renderPasswordOrCodeInput(method) {
    const application = this.getApplicationObj();
    if (method === "password") {
      return (
        <Col span={24}>
          <Form.Item
            name="password"
            className="password"
            rules={[{required: true, message: i18next.t("login:Please input your password!")}]}
          >
            <Input.Password bordered={false} placeholder={i18next.t("general:Password")} />
          </Form.Item>
        </Col>
      );
    } else if (method === "phone") {
      return (
        <Col span={24}>
          <Form.Item
            name="code"
            rules={[{required: true, message: i18next.t("login:Please input your code!")}]}
          >
            <SendCodeInput
              disabled={this.state.username?.length === 0 || !this.state.validEmailOrPhone}
              method={"login"}
              onButtonClickArgs={[this.state.username, this.state.validEmail ? "email" : "phone", Setting.getApplicationName(application)]}
              application={application}
            />
          </Form.Item>
        </Col>
      );
    } else {
      return null;
    }
  }

  renderMethodChoiceBox() {
    const isZh = Setting.getLanguage() === "zh";
    const items = [];
    if (isZh) {
      items.push({label: i18next.t("login:Verification code"), key: "phone", children: this.renderForm("phone")});
    }
    items.push({label: i18next.t("login:Account login"), key: "password", children: this.renderForm("password")});
    return (
      <div>
        <Tabs
          items={items}
          destroyInactiveTabPane
          onChange={(key) => {
            this.form.current.setFieldValue("username", "");
            this.setState({
              loginMethod: key,
              username: "",
            });
          }}
          centered
          tabBarGutter={70}
        >
        </Tabs>
      </div>
    );
    // }
  }

  renderLoginPanel(application) {
    const orgChoiceMode = application.orgChoiceMode;

    if (this.isOrganizationChoiceBoxVisible(orgChoiceMode)) {
      return this.renderOrganizationChoiceBox(orgChoiceMode);
    }

    if (this.state.getVerifyTotp !== undefined) {
      return this.state.getVerifyTotp();
    } else {
      return (
        <React.Fragment>
          {/* {this.renderSignedInBox()} */}
          {this.renderMethodChoiceBox()}
          {/* {this.renderForm(application)} */}
        </React.Fragment>
      );
    }
  }

  renderOrganizationChoiceBox(orgChoiceMode) {
    const renderChoiceBox = () => {
      switch (orgChoiceMode) {
      case "None":
        return null;
      case "Select":
        return (
          <div>
            <p style={{fontSize: "large"}}>
              {i18next.t("login:Please select an organization to sign in")}
            </p>
            <OrganizationSelect style={{width: "70%"}}
              onSelect={(value) => {
                Setting.goToLink(`/login/${value}?orgChoiceMode=None`);
              }} />
          </div>
        );
      case "Input":
        return (
          <div>
            <p style={{fontSize: "large"}}>
              {i18next.t("login:Please type an organization to sign in")}
            </p>
            <Form
              name="basic"
              onFinish={(values) => {Setting.goToLink(`/login/${values.organizationName}?orgChoiceMode=None`);}}
            >
              <Form.Item
                name="organizationName"
                rules={[{required: true, message: i18next.t("login:Please input your organization name!")}]}
              >
                <Input style={{width: "70%"}} onPressEnter={(e) => {
                  Setting.goToLink(`/login/${e.target.value}?orgChoiceMode=None`);
                }} />
              </Form.Item>
              <Button type="primary" htmlType="submit">
                {i18next.t("general:Confirm")}
              </Button>
            </Form>
          </div>
        );
      default:
        return null;
      }
    };

    return (
      <div style={{height: 300, width: 300}}>
        {renderChoiceBox()}
      </div>
    );
  }

  isOrganizationChoiceBoxVisible(orgChoiceMode) {
    if (this.state.orgChoiceMode === "None") {
      return false;
    }

    const path = this.props.match?.path;
    if (path === "/login" || path === "/login/:owner") {
      return orgChoiceMode === "Select" || orgChoiceMode === "Input";
    }

    return false;
  }

  renderBackButton() {
    if (this.state.orgChoiceMode === "None") {
      return (
        <Button type="text" size="large" icon={<ArrowLeftOutlined />}
          style={{top: "65px", left: "15px", position: "absolute"}}
          onClick={() => history.back()}>
        </Button>
      );
    }
  }

  render() {
    const application = this.getApplicationObj();
    if (application === undefined) {
      return null;
    }
    if (application === null) {
      return Util.renderMessageLarge(this, this.state.msg);
    }

    if (this.state.samlResponse !== "") {
      return <RedirectForm samlResponse={this.state.samlResponse} redirectUrl={this.state.redirectUrl} relayState={this.state.relayState} />;
    }

    if (application.signinHtml !== "") {
      return (
        <div dangerouslySetInnerHTML={{__html: application.signinHtml}} />
      );
    }

    const visibleOAuthProviderItems = (application.providers === null) ? [] : application.providers.filter(providerItem => this.isProviderVisible(providerItem));
    if (this.props.preview !== "auto" && !application.enablePassword && visibleOAuthProviderItems.length === 1) {
      Setting.goToLink(Provider.getAuthUrl(application, visibleOAuthProviderItems[0].provider, "signup"));
      return (
        <div style={{display: "flex", justifyContent: "center", alignItems: "center", width: "100%"}}>
          <Spin size="large" tip={i18next.t("login:Signing in...")} />
        </div>
      );
    }

    return (
      <React.Fragment>
        <CustomGithubCorner />
        {/* <div className="login-content" style={{margin: this.props.preview ?? this.parseOffset(application.formOffset)}}> */}
        <div className="login-content" style={{margin: "0 auto"}}>
          {/* {Setting.inIframe() || Setting.isMobile() ? null : <div dangerouslySetInnerHTML={{__html: application.formCss}} />} */}
          {/* {Setting.inIframe() || !Setting.isMobile() ? null : <div dangerouslySetInnerHTML={{__html: application.formCssMobile}} />} */}
          <div className="login-panel">
            <div className="side-image" style={{display: application.formOffset !== 4 ? "none" : null}}>
              <div dangerouslySetInnerHTML={{__html: application.formSideHtml}} />
            </div>
            <div className="login-form">
              <div>
                <div>
                  {/* {
                    Setting.renderHelmet(application)
                  }
                  {
                    Setting.renderLogo(application)
                  }
                  {
                    this.renderBackButton()
                  }
                  <LanguageSelect languages={application.organizationObj.languages} style={{top: "55px", right: "5px", position: "absolute"}} /> */}
                  {
                    this.renderLoginPanel(application)
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default LoginPage;
